import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../../helpers/api_helper';

export interface WithdrawUserRef {
  id: number;
  mobile_number: string;
}

export interface WithdrawBankRef {
  bank_name: string;
  account_number: string;
  ifsc_code?: string;
  account_holder_name?: string;
  branch_name?: string;
  account_type?: string;
  status?: string;
}

export interface WithdrawRequestItem {
  id: number;
  user: WithdrawUserRef;
  user_mobile?: string;
  amount: string; // keep as string to match API; format on UI
  status: 'pending' | 'approved' | 'completed' | 'rejected' | string;
  gateway?: string;
  bank_account: WithdrawBankRef | null;
  created_at: string;
}

export interface FetchParams {
  status?: string;
  mode?: 'upi' | 'imps_neft_rtgs' | 'qr';
  date_from?: string;
  date_to?: string;
  page?: number;
  page_size?: number;
}

export interface WithdrawRequestsResponse {
  success: boolean;
  count: number;
  page: number;
  page_size: number;
  withdrawal_requests: WithdrawRequestItem[];
}

export interface ReviewWithdrawResponse {
  success: boolean;
  message: string;
  withdrawal_request: WithdrawRequestItem;
}

export const fetchWithdrawRequests = createAsyncThunk(
  'withdrawRequests/fetch',
  async (params: FetchParams | undefined, { rejectWithValue }) => {
    try {
      const res = await api.get('/admin/withdrawal-requests/', { params });
      return res.data as WithdrawRequestsResponse;
    } catch (e: any) {
      return rejectWithValue(e.response?.data?.detail || e.message);
    }
  }
);

// Review (approve/reject) a withdrawal request
export const reviewWithdrawRequest = createAsyncThunk(
  'withdrawRequests/review',
  async (
    args: { id: number; status: 'approved' | 'rejected'; admin_notes?: string; rejection_reason?: string },
    { rejectWithValue }
  ) => {
    try {
      const payload: any = {
        status: args.status,
        admin_notes: args.admin_notes ?? ''
      };
      if (args.status === 'rejected' && args.rejection_reason) {
        payload.rejection_reason = args.rejection_reason;
      }
      const res = await api.post(`/admin/withdrawal-requests/${args.id}/review/`, payload);
      return res.data as ReviewWithdrawResponse;
    } catch (e: any) {
      const errorMessage =
        e.response?.data?.error ||
        e.response?.data?.detail ||
        e.response?.data?.message ||
        e.message ||
        'Failed to review withdrawal request';

      return rejectWithValue(errorMessage);
    }
  }
);

interface State {
  items: WithdrawRequestItem[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  error: string | null;
  activeStatus: 'all' | 'pending' | 'approved' | 'completed';
}

const initialState: State = {
  items: [],
  total: 0,
  page: 1,
  pageSize: 25,
  loading: false,
  error: null,
  activeStatus: 'all',
};

const slice = createSlice({
  name: 'withdrawRequests',
  initialState,
  reducers: {
    setActiveStatus(state, action) {
      state.activeStatus = action.payload;
    },
    setPage(state, action) {
      state.page = action.payload;
    },
    setPageSize(state, action) {
      state.pageSize = action.payload;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchWithdrawRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWithdrawRequests.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as WithdrawRequestsResponse;
        state.items = payload.withdrawal_requests;
        state.total = payload.count;
        state.page = payload.page;
        state.pageSize = payload.page_size;
      })
      .addCase(fetchWithdrawRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // review handlers
      .addCase(reviewWithdrawRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(reviewWithdrawRequest.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as ReviewWithdrawResponse;
        const updated = payload.withdrawal_request;
        const idx = state.items.findIndex(i => i.id === updated.id);
        if (idx !== -1) {
          state.items[idx] = {
            ...state.items[idx],
            ...updated,
          } as any;
        }
      })
      .addCase(reviewWithdrawRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  }
});

export const { setActiveStatus, setPage, setPageSize } = slice.actions;
export default slice.reducer;


